"use client";

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import {
    type Theme,
    type ResolvedTheme,
    readStoredTheme,
    persistTheme,
    resolveTheme,
    applyResolvedTheme,
} from "@/lib/theme";

type ThemeContextType = {
    /** The user's explicit choice — "light" | "dark" | "system". */
    theme: Theme;
    /** What's actually rendered right now — "system" resolves to "light" or "dark". */
    resolvedTheme: ResolvedTheme;
    setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
    // Lazy initializers read the DOM/localStorage synchronously on first render.
    // The boot script (see src/app/layout.tsx <head>) already set the `dark`
    // class on <html> before hydration, so reading the DOM here — rather than
    // defaulting to "light" and correcting in an effect — avoids both a
    // hydration-mismatch flash and the StrictMode double-mount clobber trap
    // (docs/conventions.md "localStorage hydrate + mirror" gotcha): there is
    // no stale initial value to accidentally persist back over real data,
    // because we never write on mount — only on explicit user action.
    const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
        if (typeof document === "undefined") return "light";
        return document.documentElement.classList.contains("dark") ? "dark" : "light";
    });

    // Listen for OS-level scheme changes while theme === "system" so the UI
    // updates live without a manual toggle or reload.
    useEffect(() => {
        if (theme !== "system") return;
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;

        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            const resolved = resolveTheme("system");
            applyResolvedTheme(resolved);
            setResolvedTheme(resolved);
        };
        // Fires only on real OS changes — this does not re-run the initial
        // resolution (that's already correct from the lazy initializer above).
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, [theme]);

    const setTheme = useCallback((next: Theme) => {
        setThemeState(next);
        persistTheme(next);
        const resolved = resolveTheme(next);
        applyResolvedTheme(resolved);
        setResolvedTheme(resolved);
    }, []);

    const value = useMemo<ThemeContextType>(
        () => ({ theme, resolvedTheme, setTheme }),
        [theme, resolvedTheme, setTheme]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextType {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
    return ctx;
}
